import { useMemo } from 'react';

import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

/**
 * Redirects legacy shop/stores/* routes to the new causes/:id/shop/* structure
 *
 * This component handles the transition from the old store-centric URLs to the new
 * cause-centric URLs where stores are a tab within the cause page.
 *
 * Examples:
 * - /shop/stores/my-stores → /home
 * - /shop/stores/david → /causes/david/shop
 * - /shop/stores/david/items/123 → /causes/david/shop/items/123
 * - /shop/stores/lincoln-high?category=memberships → /causes/lincoln-high/shop?category=memberships
 */
const RedirectLegacyStoreRoutes = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const pathnameAfterStores = useMemo(
    () => location.pathname.split('/shop/stores/')[1] || '',
    [location.pathname]
  );

  const redirectPath = useMemo(() => {
    if (!pathnameAfterStores) {
      return '/home';
    }

    // Handle /shop/stores/my-stores → redirect to /home
    if (pathnameAfterStores === 'my-stores' || pathnameAfterStores.startsWith('my-stores/')) {
      return '/home';
    }

    // Get the storeId (first segment) and remaining path
    const segments = pathnameAfterStores.split('/');
    const storeId = segments[0];

    if (!storeId) {
      return '/home';
    }

    // Get everything after the storeId (items, categories, etc.)
    const remainingPath = segments.slice(1).filter(Boolean).join('/');

    // Build new path: /causes/:causeId/shop/****
    const newPath = `/causes/${storeId}/shop${remainingPath ? `/${remainingPath}` : ''}`;

    // Preserve query params
    const search = searchParams.toString();
    return search ? `${newPath}?${search}` : newPath;
  }, [pathnameAfterStores, searchParams]);

  return <Navigate to={redirectPath} replace />;
};

export { RedirectLegacyStoreRoutes };
